import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
  Button, Input, Text, Spinner, makeStyles, tokens,
} from '@fluentui/react-components';
import { PersonAdd24Regular, Dismiss16Regular } from '@fluentui/react-icons';
import { api } from '../services/api';
import { useIsMobile } from '../hooks/useIsMobile';

interface ShareAccountDialogProps {
  accountId: number;
  accountName: string;
  open: boolean;
  onClose: () => void;
}

const useStyles = makeStyles({
  shareForm: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalM,
    alignItems: 'flex-end',
  },
  mobileShareForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalM,
  },
  shareItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  successText: { color: tokens.colorPaletteGreenForeground1 },
  errorText: { color: tokens.colorPaletteRedForeground1 },
  emptyState: {
    textAlign: 'center',
    paddingTop: tokens.spacingVerticalXL,
    paddingBottom: tokens.spacingVerticalXL,
    color: tokens.colorNeutralForeground3,
  },
});

export const ShareAccountDialog: React.FC<ShareAccountDialogProps> = ({ accountId, accountName, open, onClose }) => {
  const styles = useStyles();
  const isMobile = useIsMobile();
  const [shares, setShares] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [sharing, setSharing] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');

  const fetchShares = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getShares(accountId);
      setShares(data);
    } catch (err) {
      console.error('Failed to fetch shares:', err);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    if (open) fetchShares();
  }, [open, fetchShares]);

  const handleShare = async () => {
    if (!email.includes('@')) return;
    setSharing(true);
    setMessage('');
    try {
      const result = await api.addShareByEmail(accountId, email);
      setMessage(`Shared with ${result.display_name || email}`);
      setMessageType('success');
      setEmail('');
      fetchShares();
    } catch (err: any) {
      setMessage(err.message || 'Failed to share');
      setMessageType('error');
    } finally {
      setSharing(false);
    }
  };

  const handleRemove = async (userId: number, name: string) => {
    if (!window.confirm(`Stop sharing with ${name}?`)) return;
    try {
      await api.removeShare(accountId, userId);
      setShares(prev => prev.filter(s => s.shared_with_user_id !== userId));
      setMessage(`Removed ${name}`);
      setMessageType('success');
    } catch (err: any) {
      setMessage(err.message || 'Failed to remove');
      setMessageType('error');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
      <DialogSurface style={isMobile
        ? { maxWidth: '100vw', width: '100vw', margin: 0, borderRadius: '12px 12px 0 0', position: 'fixed', bottom: 0, maxHeight: '80vh' }
        : { maxWidth: '500px', width: '90vw' }
      }>
        <DialogTitle>Share "{accountName}"</DialogTitle>
        <DialogBody>
          <DialogContent>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3, marginBottom: '12px' }} block>
              Enter the email of a BalanceScript user to share this account with them.
            </Text>

            <div className={isMobile ? styles.mobileShareForm : styles.shareForm}>
              <div style={{ flex: 1 }}>
                <Input
                  value={email}
                  onChange={(e, d) => setEmail(d.value)}
                  placeholder="user@example.com"
                  type="email"
                  style={{ width: '100%' }}
                  disabled={sharing}
                  onKeyDown={e => { if (e.key === 'Enter') handleShare(); }}
                />
              </div>
              <Button
                appearance="primary"
                icon={<PersonAdd24Regular />}
                onClick={handleShare}
                disabled={sharing || !email.includes('@')}
                style={isMobile ? { width: '100%' } : {}}
              >
                {sharing ? 'Sharing...' : 'Share'}
              </Button>
            </div>

            {message && (
              <Text className={messageType === 'success' ? styles.successText : styles.errorText} block
                style={{ marginBottom: '12px' }}>
                {message}
              </Text>
            )}

            {loading ? (
              <Spinner size="small" label="Loading..." />
            ) : shares.length === 0 ? (
              <div className={styles.emptyState}>
                <Text size={200}>This account is not shared with anyone</Text>
              </div>
            ) : (
              <>
                <Text size={200} weight="semibold" block style={{ marginBottom: '8px' }}>
                  Shared with {shares.length} user{shares.length !== 1 ? 's' : ''}
                </Text>
                {shares.map(share => (
                  <div key={share.id} className={styles.shareItem}>
                    <div>
                      <Text weight="semibold" block>{share.display_name || 'Unknown'}</Text>
                      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{share.email}</Text>
                    </div>
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<Dismiss16Regular />}
                      onClick={() => handleRemove(share.shared_with_user_id, share.display_name)}
                      title="Remove"
                    />
                  </div>
                ))}
              </>
            )}
          </DialogContent>
        </DialogBody>
        <DialogActions>
          <Button appearance="secondary" onClick={onClose}>Close</Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};
